import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, Bot, Check, CheckCircle2, ChevronDown, Clock3, Filter,
  RotateCcw, ShieldAlert, ShieldCheck, X, XCircle,
} from 'lucide-react';
import { fetchApi } from '../api';
import { NAVIGATE_EVENT, NavigateDetail } from '../shared/notificationTargets';
import '../styles/approvals.css';

/**
 * Approvals + AI action audit screen — view key `approvals`.
 *
 * What the audit found: the approvals table was written to but had no screen of
 * its own, the deep link in the notification was dead, and `AiActionAudit` had
 * rows but no route and no reader at all.
 *
 * What this screen is: the pending queue with a real decision, and the audit
 * trail with filters, in one place — because the only useful question about an
 * approval is "what happened afterwards", and that answer lives in the audit.
 *
 * Deep link: the notification carries `entityType: 'approval'` + `entityId`.
 * `shared/notificationTargets.ts` turns that into this view plus a
 * `clientix:navigate` event with the id, which is picked up below. A full URL of
 * the form `/?approval=42#approvals` works too, which is what the alert emails
 * use — the router's allowlist only accepts a bare view key in the hash, so the
 * id has to travel in the query string.
 */

type ApprovalStatus = 'pending' | 'executing' | 'approved' | 'rejected' | 'failed';

interface ApprovalRow {
  id: number;
  action: string;
  args?: string;
  reason?: string;
  status: ApprovalStatus;
  result?: string | null;
  reviewedById?: number | null;
  reviewedAt?: string | null;
  createdAt: string;
  conversationId?: number | null;
  conversation?: { id: number; contactName?: string | null } | null;
}

interface AuditRow {
  id: number;
  action: string;
  status: string;
  args: unknown;
  result: unknown;
  conversationId: number | null;
  conversationName: string | null;
  createdAt: string;
}

interface AuditFacets {
  total: number;
  statuses: { value: string; count: number }[];
  actions: { value: string; count: number }[];
}

interface Summary {
  pending: number; executing: number; approved: number; rejected: number; failed: number; total: number;
}

const STATUS_TABS: { value: ApprovalStatus | 'all'; label: string }[] = [
  { value: 'pending', label: 'Ожидают' },
  { value: 'approved', label: 'Подтверждены' },
  { value: 'rejected', label: 'Отклонены' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'all', label: 'Все' },
];

/** Action names the AI agent can call, in Russian. */
const ACTION_LABELS: Record<string, string> = {
  create_deal: 'Создать сделку',
  update_deal: 'Изменить сделку',
  update_deal_stage: 'Передвинуть сделку по воронке',
  update_lead_details: 'Обновить карточку клиента',
  set_lead_tags: 'Проставить метки',
  escalate_to_manager: 'Позвать менеджера',
};

/** Statuses written by AiService and by ApprovalService. */
const AUDIT_STATUS_LABELS: Record<string, string> = {
  succeeded: 'Выполнено',
  rejected: 'Отклонено правилами',
  failed: 'Ошибка',
  pending_approval: 'Ждало подтверждения',
  approved_by_human: 'Подтверждено человеком',
  rejected_by_human: 'Отклонено человеком',
};

const AUDIT_STATUS_TONE: Record<string, string> = {
  succeeded: 'ok',
  approved_by_human: 'ok',
  rejected: 'warn',
  pending_approval: 'warn',
  rejected_by_human: 'muted',
  failed: 'bad',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] || action;
}

/**
 * Deep-link intent captured outside React.
 *
 * `navigateToNotification` dispatches `clientix:navigate` and App.tsx switches
 * the view in response — which means this component is usually *not mounted* at
 * the instant the id is announced. A listener inside the component would miss
 * every click that arrives from another screen. App.tsx imports this module at
 * startup, so registering here means the id is remembered from app boot and read
 * on mount.
 */
let pendingDeepLinkId: number | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener(NAVIGATE_EVENT, (event: Event) => {
    const detail = (event as CustomEvent<NavigateDetail>).detail;
    if (detail?.entityType === 'approval' && detail.entityId) pendingDeepLinkId = detail.entityId;
  });
}

function consumeDeepLinkId(): number | null {
  // `?approval=<id>` is how a full URL carries the target — the router's
  // allowlist only accepts a bare view key in the hash, so an alert email cannot
  // put the id there.
  const fromQuery = Number(new URLSearchParams(window.location.search).get('approval'));
  if (Number.isInteger(fromQuery) && fromQuery > 0) return fromQuery;
  const remembered = pendingDeepLinkId;
  pendingDeepLinkId = null;
  return remembered;
}

function formatMoment(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function parseMaybeJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  try { return JSON.parse(raw); } catch { return raw; }
}

/** Compact one-line summary of an args/result payload, for the collapsed row. */
function summarize(payload: unknown): string {
  const value = parseMaybeJson(payload);
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([key]) => key !== 'approvalId')
    .slice(0, 4)
    .map(([key, item]) => `${key}: ${typeof item === 'object' ? JSON.stringify(item) : String(item)}`);
  return entries.join(' · ');
}

export default function ApprovalsPage() {
  const initialDeepLinkId = useRef<number | null>(consumeDeepLinkId());
  // A deep link points at one specific request, which may already be decided —
  // so the tab has to start wide enough for that row to be in the list at all.
  const [statusTab, setStatusTab] = useState<ApprovalStatus | 'all'>(
    initialDeepLinkId.current ? 'all' : 'pending',
  );
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [facets, setFacets] = useState<AuditFacets | null>(null);
  const [auditStatus, setAuditStatus] = useState('');
  const [auditAction, setAuditAction] = useState('');
  const [auditSince, setAuditSince] = useState('');
  const [expandedAudit, setExpandedAudit] = useState<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(initialDeepLinkId.current);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const loadApprovals = useCallback(async (status: ApprovalStatus | 'all') => {
    const [rows, counts] = await Promise.all([
      fetchApi(`/approvals?status=${encodeURIComponent(status)}`),
      fetchApi('/approvals/summary'),
    ]);
    setApprovals(Array.isArray(rows) ? rows : []);
    setSummary(counts && typeof counts === 'object' ? counts : null);
  }, []);

  const loadAudit = useCallback(async () => {
    const params = new URLSearchParams();
    if (auditStatus) params.set('status', auditStatus);
    if (auditAction) params.set('action', auditAction);
    if (auditSince) params.set('since', new Date(auditSince).toISOString());
    params.set('limit', '50');
    const [page, auditFacets] = await Promise.all([
      fetchApi(`/ai-audit?${params.toString()}`),
      fetchApi('/ai-audit/facets'),
    ]);
    setAudit(Array.isArray(page?.items) ? page.items : []);
    setFacets(auditFacets && typeof auditFacets === 'object' ? auditFacets : null);
  }, [auditStatus, auditAction, auditSince]);

  const reload = useCallback(async () => {
    try {
      await Promise.all([loadApprovals(statusTab), loadAudit()]);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить согласования. Проверьте права доступа.');
    } finally {
      setPageLoading(false);
    }
  }, [loadApprovals, loadAudit, statusTab]);

  useEffect(() => { void reload(); }, [reload]);

  // A second notification arriving while this screen is already open.
  useEffect(() => {
    const onNavigate = (event: Event) => {
      const detail = (event as CustomEvent<NavigateDetail>).detail;
      if (detail?.entityType !== 'approval' || !detail.entityId) return;
      pendingDeepLinkId = null;
      setHighlightId(detail.entityId);
      setStatusTab('all');
    };
    window.addEventListener(NAVIGATE_EVENT, onNavigate);
    return () => window.removeEventListener(NAVIGATE_EVENT, onNavigate);
  }, []);

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightId, approvals]);

  const decide = async (id: number, decision: 'approved' | 'rejected') => {
    setBusyId(id);
    setNotice('');
    try {
      const updated = await fetchApi(`/approvals/${id}/decision`, {
        method: 'POST',
        body: JSON.stringify({ decision }),
      });
      const result = parseMaybeJson(updated?.result) as Record<string, unknown> | null;
      const overrides = result?.approvedOverrides as Record<string, unknown> | undefined;

      if (decision === 'rejected') {
        setNotice('Действие отклонено и не выполнялось. Решение записано в журнал.');
      } else if (updated?.status === 'failed') {
        setNotice('Подтверждено, но выполнить действие не удалось. Подробности — в журнале ниже.');
      } else if (overrides?.applied === true) {
        setNotice(`Подтверждено. Скидка ${overrides.approvedValue}% применена как разрешённое исключение (лимит тарифа — ${overrides.tenantLimit}%).`);
      } else if (overrides && overrides.applied === false) {
        setNotice('Подтверждено, но одобренную скидку применить не удалось — сделка сохранила урезанное значение. Проверьте журнал.');
      } else {
        setNotice('Действие подтверждено и выполнено.');
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обработать согласование');
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = summary?.pending ?? 0;

  const metrics = useMemo(() => ([
    { key: 'pending', icon: <Clock3 size={19} aria-hidden="true" />, tone: 'amber', value: pendingCount, label: 'ждут решения' },
    { key: 'approved', icon: <CheckCircle2 size={19} aria-hidden="true" />, tone: 'green', value: summary?.approved ?? 0, label: 'подтверждено' },
    { key: 'rejected', icon: <XCircle size={19} aria-hidden="true" />, tone: 'grey', value: summary?.rejected ?? 0, label: 'отклонено' },
    { key: 'failed', icon: <AlertCircle size={19} aria-hidden="true" />, tone: 'red', value: summary?.failed ?? 0, label: 'с ошибкой' },
    { key: 'audit', icon: <Bot size={19} aria-hidden="true" />, tone: 'blue', value: facets?.total ?? 0, label: 'действий ИИ в журнале' },
  ]), [pendingCount, summary, facets]);

  if (pageLoading) {
    return (
      <section className="view active approvals-page" aria-busy="true">
        <div className="ap-skeleton ap-skeleton-head" />
        <div className="ap-skeleton ap-skeleton-card" />
        <div className="ap-skeleton ap-skeleton-card" />
      </section>
    );
  }

  return (
    <section className="view active approvals-page">
      <header className="ap-hero">
        <div>
          <span className="eyebrow">Контроль ИИ-агента</span>
          <h1>Согласования и журнал действий</h1>
          <p>
            Чувствительные действия ИИ выполняются только после вашего решения.
            Отклонение действительно останавливает действие, подтверждение —
            разрешает его в том виде, в котором его запросили.
          </p>
        </div>
        <button type="button" className="ap-refresh" onClick={() => void reload()}>
          <RotateCcw size={16} aria-hidden="true" />
          Обновить
        </button>
      </header>

      {error && (
        <div className="ap-alert ap-alert-error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="ap-alert ap-alert-notice" role="status">
          <ShieldCheck size={18} aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}

      <div className="ap-metrics">
        {metrics.map((metric) => (
          <article key={metric.key}>
            <span className={`ap-metric-icon ${metric.tone}`}>{metric.icon}</span>
            <div>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
            </div>
          </article>
        ))}
      </div>

      <section className="ap-panel">
        <header className="ap-panel-head">
          <div>
            <h2>Запросы на подтверждение</h2>
            <p>ИИ запрашивает разрешение, когда действие выходит за установленные вами границы.</p>
          </div>
          <div className="ap-tabs" role="tablist" aria-label="Фильтр по статусу">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={statusTab === tab.value}
                className={`ap-tab ${statusTab === tab.value ? 'active' : ''}`}
                onClick={() => setStatusTab(tab.value)}
              >
                {tab.label}
                {tab.value === 'pending' && pendingCount > 0 && <span className="ap-tab-count">{pendingCount}</span>}
              </button>
            ))}
          </div>
        </header>

        {approvals.length === 0 ? (
          <div className="ap-empty">
            <span><CheckCircle2 size={26} aria-hidden="true" /></span>
            <div>
              <strong>Здесь пусто</strong>
              <p>
                {statusTab === 'pending'
                  ? 'Нет действий ИИ, которые требуют вашего решения.'
                  : 'В этом статусе записей нет.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="ap-list">
            {approvals.map((row) => {
              const result = parseMaybeJson(row.result) as Record<string, unknown> | null;
              const overrides = result?.approvedOverrides as Record<string, unknown> | undefined;
              const highlighted = highlightId === row.id;
              return (
                <div
                  key={row.id}
                  ref={highlighted ? highlightRef : undefined}
                  className={`ap-row ${highlighted ? 'highlight' : ''} status-${row.status}`}
                >
                  <span className="ap-row-icon"><ShieldAlert size={19} aria-hidden="true" /></span>
                  <div className="ap-row-copy">
                    <div className="ap-row-title">
                      <strong>{actionLabel(row.action)}</strong>
                      <span className={`ap-chip status-${row.status}`}>
                        {row.status === 'pending' && 'Ожидает'}
                        {row.status === 'executing' && 'Выполняется'}
                        {row.status === 'approved' && 'Подтверждено'}
                        {row.status === 'rejected' && 'Отклонено'}
                        {row.status === 'failed' && 'Ошибка'}
                      </span>
                    </div>
                    <p>{row.reason || 'ИИ запрашивает разрешение на выполнение действия'}</p>
                    <small>
                      {row.conversation?.contactName || (row.conversationId ? `Диалог #${row.conversationId}` : 'Без диалога')}
                      {' · '}создан {formatMoment(row.createdAt)}
                      {row.reviewedAt && <> · решение {formatMoment(row.reviewedAt)}</>}
                    </small>
                    {summarize(row.args) && <code className="ap-args">{summarize(row.args)}</code>}
                    {overrides?.applied === true && (
                      <small className="ap-override ok">
                        Одобрено как исключение: {String(overrides.approvedValue)}% вместо лимита {String(overrides.tenantLimit)}%.
                      </small>
                    )}
                    {overrides && overrides.applied === false && (
                      <small className="ap-override bad">
                        Одобренное значение {String(overrides.approvedValue)}% применить не удалось — сделка сохранила лимит {String(overrides.tenantLimit)}%.
                      </small>
                    )}
                    {row.status === 'rejected' && (
                      <small className="ap-override muted">Действие не выполнялось.</small>
                    )}
                  </div>
                  {row.status === 'pending' && (
                    <div className="ap-row-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={busyId === row.id}
                        onClick={() => void decide(row.id, 'approved')}
                      >
                        <Check size={15} aria-hidden="true" />
                        Подтвердить
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={busyId === row.id}
                        onClick={() => void decide(row.id, 'rejected')}
                      >
                        <X size={15} aria-hidden="true" />
                        Отклонить
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="ap-panel">
        <header className="ap-panel-head">
          <div>
            <h2>Журнал действий ИИ</h2>
            <p>Каждое действие агента и каждое ваше решение — только для чтения.</p>
          </div>
        </header>

        <div className="ap-filters">
          <span className="ap-filters-icon"><Filter size={15} aria-hidden="true" /></span>
          <label>
            <span>Статус</span>
            <select value={auditStatus} onChange={(event) => setAuditStatus(event.target.value)}>
              <option value="">Любой</option>
              {(facets?.statuses || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {(AUDIT_STATUS_LABELS[option.value] || option.value)} ({option.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Действие</span>
            <select value={auditAction} onChange={(event) => setAuditAction(event.target.value)}>
              <option value="">Любое</option>
              {(facets?.actions || []).map((option) => (
                <option key={option.value} value={option.value}>
                  {actionLabel(option.value)} ({option.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>С даты</span>
            <input type="date" value={auditSince} onChange={(event) => setAuditSince(event.target.value)} />
          </label>
          {(auditStatus || auditAction || auditSince) && (
            <button
              type="button"
              className="ap-filters-reset"
              onClick={() => { setAuditStatus(''); setAuditAction(''); setAuditSince(''); }}
            >
              Сбросить
            </button>
          )}
        </div>

        {audit.length === 0 ? (
          <div className="ap-empty">
            <span><Bot size={26} aria-hidden="true" /></span>
            <div>
              <strong>Записей нет</strong>
              <p>ИИ-агент ещё не выполнял действий, подходящих под выбранные фильтры.</p>
            </div>
          </div>
        ) : (
          <ul className="ap-audit">
            {audit.map((row) => {
              const open = expandedAudit === row.id;
              return (
                <li key={row.id} className={open ? 'open' : ''}>
                  <button
                    type="button"
                    className="ap-audit-head"
                    aria-expanded={open}
                    onClick={() => setExpandedAudit(open ? null : row.id)}
                  >
                    <span className={`ap-dot ${AUDIT_STATUS_TONE[row.status] || 'muted'}`} aria-hidden="true" />
                    <span className="ap-audit-action">{actionLabel(row.action)}</span>
                    <span className={`ap-chip audit-${AUDIT_STATUS_TONE[row.status] || 'muted'}`}>
                      {AUDIT_STATUS_LABELS[row.status] || row.status}
                    </span>
                    <span className="ap-audit-conv">
                      {row.conversationName || (row.conversationId ? `Диалог #${row.conversationId}` : '—')}
                    </span>
                    <time dateTime={row.createdAt}>{formatMoment(row.createdAt)}</time>
                    <ChevronDown size={16} aria-hidden="true" className="ap-audit-chevron" />
                  </button>
                  {open && (
                    <div className="ap-audit-body">
                      <div>
                        <h4>Параметры</h4>
                        <pre>{JSON.stringify(parseMaybeJson(row.args), null, 2)}</pre>
                      </div>
                      <div>
                        <h4>Результат</h4>
                        <pre>{JSON.stringify(parseMaybeJson(row.result), null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </section>
  );
}
